import { useEffect, useRef } from 'react';
import { useFloating, offset, flip, shift, autoUpdate, FloatingPortal } from '@floating-ui/react';
import { ArrowLeft, ArrowRight, X, ExternalLink } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';
import type { DeliverableInfo } from '@/types/api';

export interface DependencyPopoverProps {
  /**
   * The deliverable whose dependencies are being shown.
   */
  deliverableName: string;
  /**
   * List of incoming dependencies (predecessors - blocked by).
   */
  incoming: DeliverableInfo[];
  /**
   * List of outgoing dependencies (dependents - this blocks).
   */
  outgoing: DeliverableInfo[];
  /**
   * Reference element to anchor the popover to.
   */
  referenceElement: HTMLElement | null;
  /**
   * Whether the popover is open.
   */
  isOpen: boolean;
  /**
   * Callback to close the popover.
   */
  onClose: () => void;
  /**
   * Callback when a dependency is clicked to navigate to it.
   */
  onNavigate: (deliverableId: string) => void;
}

interface DependencyItemProps {
  info: DeliverableInfo;
  direction: 'incoming' | 'outgoing';
  onNavigate: (deliverableId: string) => void;
}

function DependencyItem({ info, direction, onNavigate }: DependencyItemProps) {
  const Icon = direction === 'incoming' ? ArrowLeft : ArrowRight;
  const directionLabel = direction === 'incoming' ? 'Blocked by' : 'Blocks';
  const iconColor = direction === 'incoming' ? 'text-feedback-warning' : 'text-action-primary';

  return (
    <button
      type="button"
      onClick={() => onNavigate(info.id)}
      className="
        group flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left transition-colors
        hover:bg-bg-subtle
      "
    >
      <Icon className={cn('mt-0.5 size-3.5 shrink-0', iconColor)} aria-label={directionLabel} />
      <div className="min-w-0 flex-1">
        <div
          className="
            truncate text-sm font-medium text-text-primary
            group-hover:underline
          "
        >
          {info.name}
        </div>
        <div className="truncate text-xs text-text-muted">
          {info.teamName}
          {info.featureName && ` · ${info.featureName}`}
        </div>
      </div>
      <ExternalLink
        className="
          mt-1 size-3 text-text-disabled opacity-0 transition-opacity
          group-hover:opacity-100
        "
      />
    </button>
  );
}

/**
 * DependencyPopover - A floating popover showing dependency details.
 *
 * Displays grouped lists of incoming (predecessors) and outgoing (dependents)
 * dependencies with clickable items for navigation.
 *
 * @example
 * ```tsx
 * <DependencyPopover
 *   deliverableName="User Auth API"
 *   incoming={predecessors}
 *   outgoing={dependents}
 *   referenceElement={badgeRef.current}
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onNavigate={(id) => scrollToDeliverable(id)}
 * />
 * ```
 */
export function DependencyPopover({
  deliverableName,
  incoming,
  outgoing,
  referenceElement,
  isOpen,
  onClose,
  onNavigate,
}: DependencyPopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Sync reference element with floating-ui
  useEffect(() => {
    refs.setReference(referenceElement);
  }, [referenceElement, refs]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Don't close if clicking on the reference element (badge)
      if (referenceElement?.contains(target)) return;
      // Don't close if clicking inside the popover
      if (popoverRef.current?.contains(target)) return;
      onClose();
    }

    // Handle escape key
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, referenceElement]);

  if (!isOpen) return null;

  const hasIncoming = incoming.length > 0;
  const hasOutgoing = outgoing.length > 0;

  return (
    <FloatingPortal>
      <div
        ref={(el) => {
          refs.setFloating(el);
          popoverRef.current = el;
        }}
        style={floatingStyles}
        className="z-50 max-h-80 w-72 overflow-auto rounded-lg border border-border-default bg-bg-elevated shadow-lg"
        role="dialog"
        aria-label={`Dependencies for ${deliverableName}`}
      >
        {/* Header */}
        <div
          className="
            sticky top-0 flex items-center justify-between border-b border-border-default bg-bg-elevated px-3 py-2
          "
        >
          <div className="min-w-0 pr-2">
            <span className="text-xs text-text-muted">Dependencies for</span>
            <div className="truncate text-sm font-medium text-text-primary" title={deliverableName}>
              {deliverableName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="
              rounded-sm p-1 text-text-secondary transition-colors
              hover:bg-bg-subtle hover:text-text-primary
            "
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-2">
          {/* Incoming dependencies (predecessors) */}
          {hasIncoming && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 px-2 py-1">
                <ArrowLeft className="size-3 text-feedback-warning" />
                <span className="text-xs font-medium tracking-wide text-text-muted uppercase">
                  Blocked By ({incoming.length})
                </span>
              </div>
              <div className="space-y-0.5">
                {incoming.map((dep) => (
                  <DependencyItem key={dep.id} info={dep} direction="incoming" onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          )}

          {/* Outgoing dependencies (dependents) */}
          {hasOutgoing && (
            <div>
              <div className="flex items-center gap-1.5 px-2 py-1">
                <ArrowRight className="size-3 text-action-primary" />
                <span className="text-xs font-medium tracking-wide text-text-muted uppercase">
                  Blocks ({outgoing.length})
                </span>
              </div>
              <div className="space-y-0.5">
                {outgoing.map((dep) => (
                  <DependencyItem key={dep.id} info={dep} direction="outgoing" onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          )}

          {/* Empty state (shouldn't happen, but just in case) */}
          {!hasIncoming && !hasOutgoing && (
            <div className="px-2 py-4 text-center text-sm text-text-muted">No dependencies</div>
          )}
        </div>
      </div>
    </FloatingPortal>
  );
}
